'use client';

import type { JSX, ReactNode } from 'react';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_PX: Record<AvatarSize, number> = {
  xs: 20,
  sm: 28,
  md: 40,
  lg: 56,
};

const FALLBACK_TYPO_CLASS: Record<AvatarSize, string> = {
  xs: 'avatar_initial_xs',
  sm: 'avatar_initial_sm',
  md: 'avatar_initial_md',
  lg: 'avatar_initial_lg',
};

function joinClassNames(...classes: Array<string | undefined>): string {
  return classes.filter((value): value is string => Boolean(value)).join(' ');
}

export function Avatar({
  pictureUrl,
  fallback,
  alt,
  size = 'md',
  fallbackClassName = 'bg-cfixe-light-grey text-cfixe-charcoal-text',
  className = '',
}: {
  pictureUrl?: string | null;
  fallback: ReactNode;
  alt?: string;
  size?: AvatarSize;
  fallbackClassName?: string;
  className?: string;
}): JSX.Element {
  const px = SIZE_PX[size];
  const dimension = { width: px, height: px };

  if (pictureUrl) {
    return (
      <img
        src={pictureUrl}
        alt={alt ?? ''}
        className={joinClassNames('shrink-0 rounded-full object-cover', className)}
        style={dimension}
      />
    );
  }

  return (
    <span
      className={joinClassNames(
        'flex shrink-0 items-center justify-center rounded-full',
        FALLBACK_TYPO_CLASS[size],
        fallbackClassName,
        className,
      )}
      style={dimension}
      aria-label={alt}
    >
      {fallback}
    </span>
  );
}
